import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Crown, Gift, Star, Sparkles, Clock, Phone } from 'lucide-react';

export const VIPSection = () => {
  const [vipForm, setVipForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const benefits = [
    {
      id: 'promo',
      icon: Gift,
      title: "Promociones Especiales",
      description: "Acceso anticipado a nuevos productos y promociones exclusivas"
    },
    {
      id: 'points',
      icon: Star,
      title: "Puntos de Recompensa",
      description: "Acumula puntos con cada compra y canjéalos por productos gratis"
    },
    {
      id: 'gifts',
      icon: Sparkles,
      title: "Regalos Sorpresa",
      description: "Recibe obsequios especiales en tu cumpleaños y fechas especiales"
    },
    {
      id: 'priority',
      icon: Clock,
      title: "Prioridad en Pedidos",
      description: "Tus pedidos tienen prioridad en preparación y entrega"
    },
    {
      id: 'support',
      icon: Crown,
      title: "Atención Personalizada",
      description: "Línea directa VIP para atención preferencial"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Format WhatsApp message for VIP registration
      const whatsappMessage = `🌟 *SOLICITUD MEMBRESÍA VIP* 🌟%0A%0A` +
        `👤 *Nombre:* ${vipForm.name}%0A` +
        `📱 *Teléfono:* ${vipForm.phone}%0A` +
        `📧 *Email:* ${vipForm.email}%0A%0A` +
        `Quiero unirme al programa de clientes VIP de Mr. Crunch 28 y disfrutar de todos los beneficios exclusivos! 👑`;
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/528771228589?text=${whatsappMessage}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('¡Solicitud enviada!', {
        description: 'Nos pondremos en contacto contigo pronto para activar tu membresía VIP'
      });
      
      setVipForm({ name: '', phone: '', email: '' });
    } catch (error) {
      toast.error('Error al enviar', {
        description: 'Por favor inténtalo de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setVipForm({
      ...vipForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="vip" className="py-20 bg-gradient-to-b from-black via-red-950/10 to-black relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-orange-600/10 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-600/10 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center space-x-2 mb-4">
            <Crown className="h-10 w-10 text-orange-500" />
            <h2 className="text-4xl md:text-5xl font-bold">
              Programa <span className="bg-gradient-to-r from-orange-500 via-yellow-500 to-red-600 bg-clip-text text-transparent">VIP</span>
            </h2>
            <Crown className="h-10 w-10 text-orange-500" />
          </div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Únete a nuestro exclusivo programa de clientes VIP y disfruta de beneficios especiales, descuentos y promociones diseñadas solo para ti
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.id}
                className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 border-2 border-gray-800 hover:border-orange-500/50 transition-all group hover:shadow-xl hover:shadow-orange-900/20"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-red-900/50">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* VIP Registration Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-orange-950/30 to-red-950/30 rounded-3xl p-8 md:p-12 border-2 border-orange-500/30 shadow-2xl shadow-orange-900/20">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">
                ¿Listo para ser VIP? 👑
              </h3>
              <p className="text-gray-300">
                Completa el formulario y te contactaremos para activar tu membresía VIP totalmente <span className="text-orange-500 font-bold">GRATIS</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="vip-name" className="text-gray-300 text-lg">Nombre Completo *</Label>
                <Input
                  id="vip-name"
                  name="name"
                  value={vipForm.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  className="mt-2 bg-black/50 border-orange-500/30 text-white placeholder:text-gray-500 focus:border-orange-500 h-12 text-lg"
                />
              </div>

              <div>
                <Label htmlFor="vip-phone" className="text-gray-300 text-lg">Teléfono / WhatsApp *</Label>
                <Input
                  id="vip-phone"
                  name="phone"
                  type="tel"
                  value={vipForm.phone}
                  onChange={handleChange}
                  placeholder="877 123 4567"
                  required
                  className="mt-2 bg-black/50 border-orange-500/30 text-white placeholder:text-gray-500 focus:border-orange-500 h-12 text-lg"
                />
              </div>

              <div>
                <Label htmlFor="vip-email" className="text-gray-300 text-lg">Correo Electrónico (Opcional)</Label>
                <Input
                  id="vip-email"
                  name="email"
                  type="email"
                  value={vipForm.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className="mt-2 bg-black/50 border-orange-500/30 text-white placeholder:text-gray-500 focus:border-orange-500 h-12 text-lg"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 via-yellow-500 to-red-600 hover:from-orange-600 hover:via-yellow-600 hover:to-red-700 text-black font-bold text-xl py-8 shadow-2xl shadow-orange-900/50 hover:shadow-orange-900/70 transition-all"
              >
                {isSubmitting ? 'Enviando...' : (
                  <>
                    <Crown className="mr-2 h-6 w-6" />
                    ¡Quiero ser VIP!
                    <Sparkles className="ml-2 h-6 w-6" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-400 mt-4">
                Al registrarte, aceptas recibir información sobre promociones y beneficios exclusivos
              </p>
            </form>

            {/* Contact Alternative */}
            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-center text-gray-400 mb-4">
                ¿Prefieres contactarnos directamente?
              </p>
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500/10"
                  onClick={() => window.open('https://wa.me/528771228589?text=Hola,%20quiero%20información%20sobre%20la%20membresía%20VIP', '_blank')}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  WhatsApp Directo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
